"use client";

import { useEffect, useState } from "react";

// Keep in sync with the `[data-theme]` blocks in globals.css and the /themes
// preview page. The CEO gets one place to flip through options.
export const THEMES = [
  {
    id: "default",
    name: "SNKRS",
    tagline: "Current site. White, near-black, hype green.",
    swatch: ["#ffffff", "#111113", "#12cf6e"],
  },
  {
    id: "midnight",
    name: "Midnight",
    tagline: "Dark canvas, electric green, off-hours energy.",
    swatch: ["#0a0b0d", "#f4f5f7", "#12cf6e"],
  },
  {
    id: "editorial",
    name: "Editorial",
    tagline: "Warm cream, terracotta, Playfair serif display.",
    swatch: ["#f7f1e6", "#1c1a17", "#b6532c"],
  },
  {
    id: "brutalist",
    name: "Brutalist",
    tagline: "Highlighter yellow, thick borders, Space Grotesk.",
    swatch: ["#fff44f", "#0a0a0a", "#ff4d1a"],
  },
  {
    id: "playful",
    name: "Playful",
    tagline: "Soft pastels, rounded Bricolage type, Gen-Z candy.",
    swatch: ["#fff7fb", "#2b1747", "#f45db6"],
  },
  {
    id: "noir",
    name: "Noir",
    tagline: "Pure monochrome. Inter. Everything whispers.",
    swatch: ["#fafafa", "#0a0a0a", "#525252"],
  },
  {
    id: "obsidian",
    name: "Obsidian",
    tagline: "Pitch black, off-white text. Zero decoration, all product.",
    swatch: ["#000000", "#f5f5f7", "#1f1f26"],
  },
  {
    id: "nocturne",
    name: "Nocturne",
    tagline: "Deep navy, electric cyan. Bloomberg-terminal energy.",
    swatch: ["#05091a", "#e6f0ff", "#38e7ff"],
  },
  {
    id: "carbon",
    name: "Carbon",
    tagline: "Dark charcoal, warm amber, Playfair. Whisky-bar mood.",
    swatch: ["#14110d", "#f3e9d6", "#f5b243"],
  },
] as const;

export type ThemeId = (typeof THEMES)[number]["id"];

const STORAGE_KEY = "clearline-theme";

export function applyTheme(id: ThemeId) {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {}
}

// Runs before hydration to avoid a flash of the default theme when the user
// has already picked one. Rendered inline in <head> as a <script>.
export function ThemeInitScript() {
  const js = `
try {
  var t = localStorage.getItem(${JSON.stringify(STORAGE_KEY)});
  if (t) document.documentElement.dataset.theme = t;
} catch (e) {}
`;
  return <script dangerouslySetInnerHTML={{ __html: js }} />;
}

export function ThemeSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<ThemeId>("default");

  useEffect(() => {
    const stored = (typeof window !== "undefined"
      ? localStorage.getItem(STORAGE_KEY)
      : null) as ThemeId | null;
    if (stored && THEMES.some((t) => t.id === stored)) {
      setCurrent(stored);
      document.documentElement.dataset.theme = stored;
    }
  }, []);

  function pick(id: ThemeId) {
    setCurrent(id);
    applyTheme(id);
  }

  const active = THEMES.find((t) => t.id === current) ?? THEMES[0];

  return (
    <div
      className="fixed bottom-4 right-4 z-[100] font-sans"
      style={{ fontFamily: "var(--font-sans)" }}
    >
      {open ? (
        <div
          className="mb-2 w-72 overflow-hidden rounded-2xl border border-black/10 bg-white text-black shadow-2xl"
          style={{ colorScheme: "light" }}
        >
          <div className="flex items-center justify-between border-b border-black/10 px-4 py-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-black/50">
                Theme preview
              </div>
              <div className="text-sm font-semibold">Pick a look</div>
            </div>
            <a
              href="/themes"
              className="text-[11px] font-semibold uppercase tracking-wider text-black/60 underline underline-offset-2 hover:text-black"
            >
              Gallery
            </a>
          </div>
          <ul className="max-h-[60vh] overflow-y-auto">
            {THEMES.map((t) => {
              const isActive = t.id === current;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => pick(t.id)}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                      isActive ? "bg-black/5" : "hover:bg-black/[0.03]"
                    }`}
                  >
                    <div className="flex shrink-0 overflow-hidden rounded-full border border-black/10">
                      {t.swatch.map((c, i) => (
                        <span
                          key={i}
                          className="block h-6 w-3"
                          style={{ background: c }}
                        />
                      ))}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{t.name}</span>
                        {isActive ? (
                          <span className="rounded-full bg-black px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                            On
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 text-[11px] leading-snug text-black/60">
                        {t.tagline}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-black/15 bg-white px-4 py-2.5 text-sm font-semibold text-black shadow-lg transition-transform hover:scale-[1.03]"
        style={{ colorScheme: "light" }}
        aria-label="Change theme"
      >
        <span className="flex overflow-hidden rounded-full border border-black/10">
          {active.swatch.map((c, i) => (
            <span
              key={i}
              className="block h-4 w-2"
              style={{ background: c }}
            />
          ))}
        </span>
        <span>Theme: {active.name}</span>
        <span className="text-black/40">{open ? "×" : "▾"}</span>
      </button>
    </div>
  );
}
